import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import hrService from '../../../services/hrService';

const AdminQuickActions = () => {
  const navigate = useNavigate();
  const [processingAction, setProcessingAction] = useState(null);
  const [error, setError] = useState('');
  const [employees, setEmployees] = useState([]);
  const [showEmployeeList, setShowEmployeeList] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadEmployees = async () => {
    setLoading(true);
    setError('');
    try {
      const employeeData = await hrService?.getAllEmployees();
      setEmployees(employeeData || []);
    } catch (err) {
      console.error('Error loading employees:', err);
      setError('Failed to load employee list');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showEmployeeList) {
      loadEmployees();
    }
  }, [showEmployeeList]);

  const handleProcessLeaveBalance = async () => {
    setProcessingAction('leave-balance');
    setError('');
    try {
      const result = await hrService?.processMonthlyLeaveBalance();
      // Show success notification - you could use a toast library here
      console.log(`Processed leave balance for ${result?.processedCount} employees`);
    } catch (err) {
      console.error('Error processing leave balance:', err);
      setError('Failed to process leave balance');
    } finally {
      setProcessingAction(null);
    }
  };

  const handleNavigateToReports = () => {
    // For now, show available reports options
    const reportTypes = [
      'Employee Attendance Report',
      'Leave Balance Report', 
      'Monthly Targets Report',
      'Payroll Summary Report'
    ];
    
    // You could implement a modal or dropdown here
    console.log('Available reports:', reportTypes);
    // For demo, navigate to attendance which has some reporting features
    navigate('/attendance-tracking');
  };

  const handleNavigateToPayroll = () => {
    // Navigate to a payroll management page (you'd need to create this)
    // For now, navigate to existing salary/targets page
    navigate('/employee-dashboard'); // or create dedicated payroll page
  };

  const handleNavigateToSettings = () => {
    // Navigate to system settings (you'd need to create this)
    console.log('Navigate to system settings');
    // Could implement settings modal or dedicated page
  };

  const handleSoftDeleteEmployee = async (employeeId, employeeName) => {
    if (!confirm(`Are you sure you want to permanently delete ${employeeName}? This action cannot be undone.`)) {
      return;
    }

    setProcessingAction(`delete-${employeeId}`);
    try {
      await hrService?.deleteEmployee(employeeId);
      // Reload the employee list
      await loadEmployees();
    } catch (err) {
      console.error('Error deleting employee:', err);
      setError('Failed to delete employee');
    } finally {
      setProcessingAction(null);
    }
  };

  const quickActions = [
    {
      id: 'manage-employee',
      title: 'Manage Employee',
      description: 'View and manage all employees',
      icon: 'Users',
      color: 'primary',
      action: () => setShowEmployeeList(!showEmployeeList),
      disabled: false
    },
    {
      id: 'upload-attendance',
      title: 'Upload Attendance',
      description: 'Bulk upload attendance data',
      icon: 'Upload',
      color: 'success',
      action: () => navigate('/attendance-tracking'),
      disabled: false
    },
    {
      id: 'approve-leaves',
      title: 'Approve Leaves',
      description: 'Review pending leave requests',
      icon: 'CheckCircle',
      color: 'warning',
      action: () => navigate('/leave-management'),
      disabled: false
    },
    {
      id: 'generate-reports',
      title: 'Generate Reports',
      description: 'Create HR analytics reports',
      icon: 'FileText',
      color: 'accent',
      action: handleNavigateToReports,
      disabled: false
    },
    {
      id: 'manage-payroll',
      title: 'Manage Payroll',
      description: 'Process salary and incentives',
      icon: 'DollarSign',
      color: 'success',
      action: handleNavigateToPayroll,
      disabled: false
    },
    {
      id: 'process-leave-balance',
      title: 'Process Leave Balance',
      description: 'Calculate monthly leave accruals',
      icon: 'Calendar',
      color: 'primary',
      action: handleProcessLeaveBalance,
      disabled: processingAction === 'leave-balance'
    },
    {
      id: 'system-settings',
      title: 'System Settings',
      description: 'Configure HR portal settings',
      icon: 'Settings',
      color: 'primary',
      action: handleNavigateToSettings,
      disabled: false
    }
  ];

  return (
    <div className="bg-card rounded-lg border border-border card-shadow p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-foreground">Quick Actions</h2>
        <Icon name="Zap" size={20} color="var(--color-primary)" />
      </div>
      
      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {quickActions?.map((action) => (
          <button
            key={action?.id}
            onClick={action?.action}
            disabled={action?.disabled}
            className={`
              group relative overflow-hidden
              ${action?.color === 'primary' ? 'bg-primary/5 hover:bg-primary/10 border-primary/10 hover:border-primary/20' : ''}
              ${action?.color === 'success' ? 'bg-green-50 hover:bg-green-100 border-green-200 hover:border-green-300' : ''}
              ${action?.color === 'warning' ? 'bg-yellow-50 hover:bg-yellow-100 border-yellow-200 hover:border-yellow-300' : ''}
              ${action?.color === 'accent' ? 'bg-blue-50 hover:bg-blue-100 border-blue-200 hover:border-blue-300' : ''}
              border rounded-lg p-4 text-left transition-all duration-200
              hover:shadow-md hover:scale-105
              disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
              ${action?.id === 'employee-list' && showEmployeeList ? 'ring-2 ring-primary' : ''}
            `}
          >
            {processingAction === action?.id && (
              <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
                <Icon name="Loader2" size={20} className="animate-spin" />
              </div>
            )}
            
            <div className="flex items-start space-x-3">
              <div className={`
                w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-200
                ${action?.color === 'primary' ? 'bg-primary/10 group-hover:bg-primary/20' : ''}
                ${action?.color === 'success' ? 'bg-green-100 group-hover:bg-green-200' : ''}
                ${action?.color === 'warning' ? 'bg-yellow-100 group-hover:bg-yellow-200' : ''}
                ${action?.color === 'accent' ? 'bg-blue-100 group-hover:bg-blue-200' : ''}
              `}>
                <Icon 
                  name={action?.icon} 
                  size={20} 
                  color={`var(--color-${action?.color})`} 
                />
              </div>
              
              <div className="flex-1">
                <h3 className={`
                  font-medium mb-1 transition-colors duration-200
                  ${action?.color === 'primary' ? 'text-primary group-hover:text-primary' : ''}
                  ${action?.color === 'success' ? 'text-green-600 group-hover:text-green-700' : ''}
                  ${action?.color === 'warning' ? 'text-yellow-600 group-hover:text-yellow-700' : ''}
                  ${action?.color === 'accent' ? 'text-blue-600 group-hover:text-blue-700' : ''}
                `}>
                  {action?.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {action?.description}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Employee List Section */}
      {showEmployeeList && (
        <div className="mt-6 border-t border-border pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-foreground">Employee List</h3>
            <div className="flex space-x-2">
              <button
                onClick={() => navigate('/employee-profile')}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-200 flex items-center space-x-2"
              >
                <Icon name="UserPlus" size={16} />
                <span>Add New Employee</span>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader2" size={24} className="animate-spin" />
              <span className="ml-2 text-muted-foreground">Loading employees...</span>
            </div>
          ) : employees?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="Users" size={48} color="var(--color-muted)" className="mx-auto mb-2" />
              <p className="text-muted-foreground">No employees found</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Employee
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Department
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Position
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-background divide-y divide-border">
                    {employees?.map((employee) => (
                      <tr key={employee?.id} className="hover:bg-muted/50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                <span className="text-sm font-medium text-primary">
                                  {employee?.userProfiles?.fullName?.charAt(0) || employee?.name?.charAt(0) || 'U'}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-foreground">
                                {employee?.userProfiles?.fullName || employee?.name || 'Unknown'}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {employee?.employeeId || 'No ID'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {employee?.department || 'Not specified'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {employee?.position || 'Not specified'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            employee?.employmentStatus === 'active' ?'bg-green-100 text-green-800' :'bg-red-100 text-red-800'
                          }`}>
                            {employee?.employmentStatus || 'active'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => navigate(`/employee-profile?id=${employee?.id}`)}
                              className="text-blue-600 hover:text-blue-700 transition-colors duration-200"
                              title="Update Employee"
                            >
                              <Icon name="Edit" size={16} />
                            </button>
                            <button
                              onClick={() => navigate(`/employee-profile?id=${employee?.id}`)}
                              className="text-green-600 hover:text-green-700 transition-colors duration-200"
                              title="View Employee"
                            >
                              <Icon name="Eye" size={16} />
                            </button>
                            <button
                              onClick={() => handleSoftDeleteEmployee(employee?.id, employee?.userProfiles?.fullName || employee?.name)}
                              disabled={processingAction === `delete-${employee?.id}`}
                              className="text-red-600 hover:text-red-700 transition-colors duration-200 disabled:opacity-50"
                              title="Delete Employee"
                            >
                              {processingAction === `delete-${employee?.id}` ? (
                                <Icon name="Loader2" size={16} className="animate-spin" />
                              ) : (
                                <Icon name="Trash2" size={16} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminQuickActions;